import { useEffect, useRef, useState } from 'react'
import type { LineItem, CreateLineItemPayload } from '@/types'

// --- Row ordering ----------------------------------------------------------
// Rows must render in the order the estimator entered them. The backend already
// stamps an ascending `sortOrder` on create (max + 1) and returns line items
// `orderBy: { sortOrder: 'asc' }` — but this panel used to re-sort DESCENDING
// (`b.sortOrder - a.sortOrder`), which reversed entry order and disagreed with
// the PDF (which renders the backend's ascending order). Worse, the `?? 0`
// fallback collapsed rows with a null/0 sortOrder into a tie, and Postgres
// returns tied rows in an arbitrary order that shifts after an UPDATE — so the
// order also changed as rows were edited. Hence "sometimes out of order".
//
// Sort ascending, and break ties deterministically so a tie can never reshuffle
// between renders, or between the screen and the generated PDF.

export function compareRows(a: LineItem, b: LineItem): number {
  const sortDiff = (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
  if (sortDiff !== 0) return sortDiff

  // Tie-break on creation time — the closest proxy for entry order.
  const aTime = Date.parse(a.createdAt ?? '')
  const bTime = Date.parse(b.createdAt ?? '')
  if (!isNaN(aTime) && !isNaN(bTime) && aTime !== bTime) return aTime - bTime

  // Last resort: arbitrary, but *consistent* across renders.
  return a.id.localeCompare(b.id)
}

// The unit-price rows for this estimate, in entry order.
// Copies before sorting — Array.prototype.sort mutates, and `lineItems` is store state.
export function sortUnitPriceRows(lineItems: LineItem[]): LineItem[] {
  return lineItems.filter((li) => li.section === 'unit-price').sort(compareRows)
}

// Move the row at `from` to index `to`, returning a new array.
// Out-of-range or no-op moves return the input order unchanged.
export function moveRow<T>(rows: T[], from: number, to: number): T[] {
  if (from === to) return rows
  if (from < 0 || from >= rows.length || to < 0 || to >= rows.length) return rows
  const next = rows.slice()
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

// The reorder endpoint renumbers sortOrder to 0..n-1 across exactly the ids it
// receives. Sending only the unit-price ids would renumber them into the same
// range as other sections' rows and scramble the estimate, so always send EVERY
// line item id — substituting the newly-ordered unit-price ids into the slots
// the unit-price rows already occupy.
export function buildGlobalOrder(lineItems: LineItem[], orderedUnitPriceIds: string[]): string[] {
  const queue = orderedUnitPriceIds.slice()
  // Sort into the canonical global order first: the endpoint assigns sortOrder
  // from each id's INDEX in this list, so building it from the store's array
  // order (which is append order after edits, not sortOrder) would renumber
  // other sections' rows and reorder them as a side effect.
  return lineItems
    .slice()
    .sort(compareRows)
    .map((li) => (li.section === 'unit-price' ? (queue.shift() ?? li.id) : li.id))
}

// --- Money input helpers ---------------------------------------------------
// The Unit Price field used to bind its `value` to the parsed numeric rate.
// That made it impossible to type cents: the moment a "." was typed, the value
// round-tripped through parseFloat ("12." -> 12) and React re-rendered the
// field without the decimal point. These helpers keep the raw typed string as
// the displayed value while editing, so cents survive. See unit-price-panel.test.ts.

// Keep digits and a single decimal point, capped at 2 decimal places.
// Preserves in-progress values like "12." and "12.50".
export function sanitizeMoneyInput(raw: string): string {
  let cleaned = raw.replace(/[^0-9.]/g, '')
  const firstDot = cleaned.indexOf('.')
  if (firstDot !== -1) {
    const whole = cleaned.slice(0, firstDot)
    // drop any further decimal points, then cap to 2 decimals
    const dec = cleaned.slice(firstDot + 1).replace(/\./g, '').slice(0, 2)
    cleaned = whole + '.' + dec
  }
  return cleaned
}

// On blur, pad to two decimals so entered cents stay visible ("12.5" -> "12.50").
// Blank/zero collapses to '' so the placeholder shows.
export function normalizeMoneyOnBlur(raw: string): string {
  const n = parseFloat(raw)
  if (!raw || isNaN(n) || n === 0) return ''
  return n.toFixed(2)
}

// String shown in the field when it is not actively being edited.
export function formatRateValue(rate: number | null | undefined): string {
  if (!rate) return ''
  return String(rate)
}
// ---------------------------------------------------------------------------

interface UnitPricePanelProps {
  estimateId: string
  lineItems: LineItem[]
  onAddItem: (payload: CreateLineItemPayload) => Promise<void>
  onUpdateItem: (itemId: string, data: Partial<LineItem>) => void
  onDeleteItem: (itemId: string) => void
  // Receives EVERY line item id in its new global order (see buildGlobalOrder).
  onReorderItems?: (orderedIds: string[]) => void
}

const UNIT_TYPES = [
  'Square Foot', 'Linear Foot', 'Each', 'Cubic Foot', 'Cubic Yard',
  'Lump Sum', 'Load', 'Hour', 'Day',
]

const XCircleIcon = () => (
  <svg style={{ width: 20, height: 20 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M15 9l-6 6" />
    <path d="M9 9l6 6" />
  </svg>
)

const ChevronDown = () => (
  <svg className="up-select-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
)

// Six-dot grip, matching the writeup panel's drag affordance (.wu-drag).
const GripIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="9" cy="6" r="1.6" />
    <circle cx="15" cy="6" r="1.6" />
    <circle cx="9" cy="12" r="1.6" />
    <circle cx="15" cy="12" r="1.6" />
    <circle cx="9" cy="18" r="1.6" />
    <circle cx="15" cy="18" r="1.6" />
  </svg>
)

export function UnitPricePanel({
  estimateId,
  lineItems,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
  onReorderItems,
}: UnitPricePanelProps) {
  void estimateId
  const rows = sortUnitPriceRows(lineItems)

  // Drag-to-reorder state. `dragId` is the row being dragged; `overId` is the
  // row it would drop onto (used to draw the insertion highlight).
  //
  // The dragged id is ALSO held in a ref: drop handlers must read it
  // synchronously, and React state is not guaranteed to have flushed by the
  // time the drop event fires. State drives the visuals, the ref drives the
  // actual reorder.
  const dragIdRef = useRef<string | null>(null)
  const [dragId, setDragId] = useState<string | null>(null)
  const [overId, setOverId] = useState<string | null>(null)
  // Rows are only draggable while the grip is held — otherwise the browser's
  // native row drag would hijack text selection inside the inputs.
  const [gripRowId, setGripRowId] = useState<string | null>(null)

  const canReorder = !!onReorderItems && rows.length > 1

  function endDrag() {
    dragIdRef.current = null
    setDragId(null)
    setOverId(null)
    setGripRowId(null)
  }

  function handleDrop(targetId: string) {
    const draggedId = dragIdRef.current
    if (!draggedId || !onReorderItems) return endDrag()

    const from = rows.findIndex((r) => r.id === draggedId)
    const to = rows.findIndex((r) => r.id === targetId)
    endDrag()
    if (from === -1 || to === -1 || from === to) return

    const orderedIds = moveRow(rows, from, to).map((r) => r.id)
    onReorderItems(buildGlobalOrder(lineItems, orderedIds))
  }

  // Raw, in-progress text for each row's Unit Price field, keyed by row id.
  // Holds what the user is typing (e.g. "12.") so the decimal point isn't
  // erased by the numeric round-trip. Falls back to the stored rate when absent.
  const [rateDrafts, setRateDrafts] = useState<Record<string, string>>({})

  // Disarm the grip if the mouse is released anywhere else — otherwise pressing
  // the grip and letting go off-row would leave that row permanently draggable.
  useEffect(() => {
    if (!gripRowId) return
    const clear = () => setGripRowId(null)
    window.addEventListener('mouseup', clear)
    return () => window.removeEventListener('mouseup', clear)
  }, [gripRowId])

  // Auto-create a first row if none exist
  useEffect(() => {
    if (rows.length === 0) {
      onAddItem({
        description: '',
        category: 'misc',
        quantity: 0,
        unit: 'Each',
        rate: 0,
        section: 'unit-price',
        notes: '',
      })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleAddRow() {
    await onAddItem({
      description: '',
      category: 'misc',
      quantity: 0,
      unit: 'Each',
      rate: 0,
      section: 'unit-price',
      notes: '',
    })
  }

  function handleFieldChange(itemId: string, field: string, value: string | number) {
    const data: Partial<LineItem> = {}

    if (field === 'item') {
      data.notes = value as string
    } else if (field === 'description') {
      data.description = value as string
    } else if (field === 'unit') {
      data.unit = value as string
    } else if (field === 'rate') {
      const numVal = typeof value === 'string' ? parseFloat(value) || 0 : value
      const existing = rows.find((r) => r.id === itemId)
      const qty = existing?.quantity || 0
      data.rate = numVal
      data.lineTotal = numVal * qty
    } else if (field === 'quantity') {
      const numVal = typeof value === 'string' ? parseFloat(value) || 0 : value
      const existing = rows.find((r) => r.id === itemId)
      const rate = existing?.rate || 0
      data.quantity = numVal
      data.lineTotal = numVal * rate
    }

    onUpdateItem(itemId, data)
  }

  const grandTotal = rows.reduce((sum, r) => sum + (r.lineTotal || 0), 0)

  const fmt = (n: number) =>
    '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  return (
    <div className="up-panel">
      <div className="card">
        <table className="tbl up-tbl">
          <thead>
            <tr>
              {canReorder && <th className="up-col-drag" />}
              <th className="up-col-item">Item</th>
              <th className="up-col-desc">Description</th>
              <th className="up-col-unit">Unit Type</th>
              <th className="up-col-price">Unit Price</th>
              <th className="up-col-qty">Quantity</th>
              <th className="up-col-total">Total</th>
              <th style={{ width: 48 }} />
            </tr>
          </thead>
          <tbody>
            {/* Add New Line Item row */}
            <tr className="up-add-row-tr" onClick={handleAddRow} style={{ cursor: 'pointer' }}>
              <td colSpan={canReorder ? 8 : 7}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="up-plus-circle">+</span>
                  <span className="up-add-label">Add New Line Item</span>
                </div>
              </td>
            </tr>

            {rows.map((row) => (
              <tr
                key={row.id}
                className={[
                  dragId === row.id ? 'up-row-dragging' : '',
                  overId === row.id && dragId !== row.id ? 'up-row-over' : '',
                ].filter(Boolean).join(' ')}
                draggable={canReorder && gripRowId === row.id}
                onDragStart={(e) => {
                  dragIdRef.current = row.id
                  setDragId(row.id)
                  e.dataTransfer.effectAllowed = 'move'
                  // Firefox refuses to start a drag without payload data.
                  e.dataTransfer.setData('text/plain', row.id)
                }}
                onDragOver={(e) => {
                  if (!dragId) return
                  e.preventDefault() // required to allow a drop
                  e.dataTransfer.dropEffect = 'move'
                  if (overId !== row.id) setOverId(row.id)
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  handleDrop(row.id)
                }}
                onDragEnd={endDrag}
              >
                {canReorder && (
                  <td
                    className="up-drag-cell"
                    // Arm dragging only while the grip is held, so clicking into
                    // a field still selects text normally.
                    onMouseDown={() => setGripRowId(row.id)}
                    onMouseUp={() => setGripRowId(null)}
                  >
                    <span
                      className="up-drag"
                      role="button"
                      tabIndex={-1}
                      aria-label={`Reorder ${row.notes || row.description || 'line item'}`}
                      title="Drag to reorder"
                    >
                      <GripIcon />
                    </span>
                  </td>
                )}
                <td>
                  <input
                    className="up-field"
                    type="text"
                    value={row.notes || ''}
                    placeholder="Item name"
                    onChange={(e) => handleFieldChange(row.id, 'item', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    className="up-field"
                    type="text"
                    value={row.description || ''}
                    placeholder="Description"
                    onChange={(e) => handleFieldChange(row.id, 'description', e.target.value)}
                  />
                </td>
                <td>
                  <div className="up-select-wrap">
                    <select
                      className="up-field up-field-select"
                      value={row.unit || 'Each'}
                      onChange={(e) => handleFieldChange(row.id, 'unit', e.target.value)}
                    >
                      {UNIT_TYPES.map((ut) => (
                        <option key={ut} value={ut}>{ut}</option>
                      ))}
                    </select>
                    <ChevronDown />
                  </div>
                </td>
                <td>
                  <div className="up-dollar-wrap">
                    <span className="up-dollar-sign">$</span>
                    <input
                      className="up-field up-field-dollar"
                      type="text"
                      inputMode="decimal"
                      value={rateDrafts[row.id] ?? formatRateValue(row.rate)}
                      placeholder="0.00"
                      onChange={(e) => {
                        const val = sanitizeMoneyInput(e.target.value)
                        setRateDrafts((d) => ({ ...d, [row.id]: val }))
                        handleFieldChange(row.id, 'rate', val)
                      }}
                      onBlur={() => {
                        setRateDrafts((d) => ({
                          ...d,
                          [row.id]: normalizeMoneyOnBlur(d[row.id] ?? formatRateValue(row.rate)),
                        }))
                      }}
                    />
                  </div>
                </td>
                <td>
                  <input
                    className="up-field"
                    type="text"
                    inputMode="numeric"
                    value={row.quantity || ''}
                    placeholder="0"
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, '')
                      handleFieldChange(row.id, 'quantity', val)
                    }}
                  />
                </td>
                <td style={{ fontWeight: 600, color: 'var(--s900)', whiteSpace: 'nowrap' }}>
                  {fmt(row.lineTotal || 0)}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    className="tbl-action-btn tbl-action-btn--danger"
                    title="Delete"
                    onClick={() => onDeleteItem(row.id)}
                  >
                    <XCircleIcon />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={canReorder ? 6 : 5} style={{ textAlign: 'right', fontWeight: 600, color: 'var(--s600)' }}>
                  Total
                </td>
                <td style={{ fontWeight: 700, fontSize: '15px', color: 'var(--s900)', whiteSpace: 'nowrap' }}>
                  {fmt(grandTotal)}
                </td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  )
}
